import clsx from 'clsx';
import { useRef, useState } from 'react';

import { Button, Icon, IconButton, Switch, TextField, Typography } from '@/ui';

import ConnectionStatus from '../ConnectionStatus';
import { LoadError, PageLoading } from '../PageState';
import { Toast, useToast } from '../Toast';

import type { Exclusion, Trigger } from '@/store/api/mcpMemberApi';
import type { KeyboardEvent } from 'react';

import {
  useAddCoachingNotesExclusionMutation,
  useDeleteCoachingNotesExclusionMutation,
  useDeleteCoachingNotesTriggerMutation,
  useGetCoachingNotesSettingsQuery,
  useGetMcpConnectGrantsQuery,
  useUpsertCoachingNotesTriggerMutation,
} from '@/store/api/mcpMemberApi';

export const MAX_MOMENT_TEXT = 200;

interface Draft {
  id: string | null;
  then: string;
  when: string;
}

const rowClass =
  'grid items-center gap-3 rounded-2xl bg-white p-4 shadow md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto]';
const onEnter = (action: () => void) => (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    action();
  }
};

const AiMomentsPage = () => {
  const settingsQuery = useGetCoachingNotesSettingsQuery();
  const grantsQuery = useGetMcpConnectGrantsQuery();
  const [upsertTrigger, { isLoading: savingTrigger }] =
    useUpsertCoachingNotesTriggerMutation();
  const [deleteTrigger] = useDeleteCoachingNotesTriggerMutation();
  const [addExclusion] = useAddCoachingNotesExclusionMutation();
  const [deleteExclusion] = useDeleteCoachingNotesExclusionMutation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [exclusionText, setExclusionText] = useState('');
  // Enter can fire again (repeat, double press) before the request settles.
  const submitting = useRef({ exclusion: false, trigger: false });
  const { close, show, toast } = useToast();

  if (settingsQuery.isLoading) return <PageLoading />;
  if (settingsQuery.isError || !settingsQuery.data) {
    return (
      <LoadError
        onRetry={() => {
          settingsQuery.refetch().catch(() => undefined);
        }}
      />
    );
  }
  const { description, exclusions, triggers } = settingsQuery.data;
  const failed = () => show('Couldn’t save. Try again.'); // NEW COPY
  const run = (action: () => Promise<void>) => () => {
    action().catch(failed);
  };

  const saveDraft = async () => {
    if (!draft) return;
    const when = draft.when.trim();
    const then = draft.then.trim();
    if (!when || !then) {
      show('Fill in both parts first');
      return;
    }
    if (submitting.current.trigger) return;
    const existing = triggers.find((trigger) => trigger.id === draft.id);
    submitting.current.trigger = true;
    try {
      await upsertTrigger({
        enabled: existing?.enabled ?? true,
        id: existing?.id,
        then,
        when,
      }).unwrap();
    } finally {
      submitting.current.trigger = false;
    }
    setDraft(null);
    show('Saved. Your AI will use this from now on.');
  };
  // A toggle sends the cached when/then; wait for the refetch after any save so
  // it never writes back a pre-edit version.
  const togglesLocked = savingTrigger || settingsQuery.isFetching;
  const toggle = async (trigger: Trigger) => {
    if (togglesLocked) return;
    await upsertTrigger({
      enabled: !trigger.enabled,
      id: trigger.id,
      then: trigger.then,
      when: trigger.when,
    }).unwrap();
  };
  const remove = async (trigger: Trigger) => {
    await deleteTrigger({ id: trigger.id }).unwrap();
    show(
      'Moment removed',
      run(async () => {
        await upsertTrigger({
          enabled: trigger.enabled,
          position: trigger.position,
          then: trigger.then,
          when: trigger.when,
        }).unwrap();
      }),
    );
  };
  const addEx = async () => {
    const text = exclusionText.trim();
    if (!text || submitting.current.exclusion) return;
    submitting.current.exclusion = true;
    try {
      await addExclusion({ text }).unwrap();
    } finally {
      submitting.current.exclusion = false;
    }
    setExclusionText('');
  };
  const removeEx = async (exclusion: Exclusion) => {
    await deleteExclusion({ id: exclusion.id }).unwrap();
    show(
      'Removed',
      run(async () => {
        await addExclusion({ text: exclusion.text }).unwrap();
      }),
    );
  };

  const editor = (current: Draft) => (
    <div key={current.id ?? 'new'} className={rowClass}>
      <TextField
        inputProps={{ maxLength: MAX_MOMENT_TEXT }}
        label="When this happens"
        size="small"
        value={current.when}
        autoFocus
        onChange={(event) => setDraft({ ...current, when: event.target.value })}
        onKeyDown={onEnter(run(saveDraft))}
      />
      <Icon>arrow_forward</Icon>
      <TextField
        inputProps={{ maxLength: MAX_MOMENT_TEXT }}
        label="Remind me of this"
        size="small"
        value={current.then}
        onChange={(event) => setDraft({ ...current, then: event.target.value })}
        onKeyDown={onEnter(run(saveDraft))}
      />
      <div className="flex gap-2">
        <Button size="small" variant="text" onClick={() => setDraft(null)}>
          Cancel
        </Button>
        <Button
          loading={savingTrigger}
          size="small"
          variant="contained"
          onClick={run(saveDraft)}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <Typography component="h1" variant="h4">
          When your AI brings this up
        </Typography>
        <Typography className="mt-2 text-cement-400" variant="body1">
          The moments when your AI checks your coaching notes, and what it
          should remind you of. Written from your sessions. Make them yours.
        </Typography>
      </div>
      {grantsQuery.data?.grants.length === 0 && (
        <ConnectionStatus grants={[]} settings={undefined} />
      )}
      <section className="mb-8 flex flex-col gap-3">
        {triggers.map((trigger) =>
          draft?.id === trigger.id ? (
            editor(draft)
          ) : (
            <div
              key={trigger.id}
              aria-label={trigger.when}
              className={clsx(rowClass, { 'opacity-60': !trigger.enabled })}
              role="group"
            >
              <div>
                <small className="block text-cement-300">
                  When this happens
                </small>
                <div>{trigger.when}</div>
              </div>
              <Icon>arrow_forward</Icon>
              <div>
                <small className="block text-cement-300">
                  Remind me of this
                </small>
                <div>{trigger.then}</div>
              </div>
              <div className="flex items-center gap-1">
                <IconButton
                  aria-label="Edit"
                  size="small"
                  onClick={() =>
                    setDraft({
                      id: trigger.id,
                      then: trigger.then,
                      when: trigger.when,
                    })
                  }
                >
                  <Icon>edit</Icon>
                </IconButton>
                <IconButton
                  aria-label="Remove"
                  size="small"
                  onClick={run(() => remove(trigger))}
                >
                  <Icon>close</Icon>
                </IconButton>
                <Switch
                  checked={trigger.enabled}
                  disabled={togglesLocked}
                  inputProps={{ 'aria-label': 'Turn on', role: 'switch' }}
                  onChange={run(() => toggle(trigger))}
                />
              </div>
            </div>
          ),
        )}
        {draft?.id === null && editor(draft)}
        <div>
          <Button
            startIcon={<Icon>add</Icon>}
            variant="text"
            onClick={() => setDraft({ id: null, then: '', when: '' })}
          >
            Add a moment
          </Button>
        </div>
      </section>
      <section aria-labelledby="keep-out-heading" className="mb-8">
        <Typography component="h2" id="keep-out-heading" variant="h6">
          Keep it out of these
        </Typography>
        <Typography className="mb-3 text-cement-400" variant="body2">
          Your AI won’t bring up your coaching here, even if it seems related.
        </Typography>
        <div className="mb-3 flex flex-wrap gap-2">
          {exclusions.map((exclusion) => (
            <span
              key={exclusion.id}
              className="inline-flex items-center gap-1 rounded-full bg-white py-1 pl-3 pr-1 shadow-sm"
            >
              {exclusion.text}
              <IconButton
                aria-label="Remove"
                size="small"
                onClick={run(() => removeEx(exclusion))}
              >
                <Icon>close</Icon>
              </IconButton>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <TextField
            inputProps={{
              'aria-label': 'Add a place to keep coaching out of',
              maxLength: MAX_MOMENT_TEXT,
            }}
            placeholder="For example, work chats with my team"
            size="small"
            value={exclusionText}
            onChange={(event) => setExclusionText(event.target.value)}
            onKeyDown={onEnter(run(addEx))}
          />
          <Button size="small" variant="contained" onClick={run(addEx)}>
            Add
          </Button>
        </div>
      </section>
      <details className="rounded-2xl bg-white p-4 shadow">
        <summary className="cursor-pointer font-semibold">
          How your AI reads this
        </summary>
        <p className="text-sm text-cement-400">
          Mento turns your moments into the description your AI uses to decide
          when to look. It updates after each session and whenever you edit.
        </p>
        <pre
          className="whitespace-pre-wrap text-sm"
          data-testid="description-preview"
        >{`mento_coaching_notes\n${description.text}`}</pre>
      </details>
      <Toast toast={toast} onClose={close} />
    </div>
  );
};

export default AiMomentsPage;
